<script setup lang="ts">
import { CheckCircle2, PlayCircle } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import type { FeatureGuideStep } from '~/types/guidedSetup';

const props = withDefaults(defineProps<{
    modelValue: boolean;
    title: string;
    description?: string;
    steps: ReadonlyArray<FeatureGuideStep>;
    loading?: boolean;
    startLabel?: string;
    nextLabel?: string;
    backLabel?: string;
    skipLabel?: string;
    emptyTitle?: string;
    emptyDescription?: string;
}>(), {
    description: '',
    loading: false,
    startLabel: 'Start setup',
    nextLabel: 'Next',
    backLabel: 'Back',
    skipLabel: 'Skip guide',
    emptyTitle: 'Guide unavailable',
    emptyDescription: 'This guide does not have any steps yet.',
});

const emit = defineEmits<{
    'update:modelValue': [value: boolean];
    dismiss: [];
    start: [];
    stepChange: [step: FeatureGuideStep, index: number];
}>();

const currentIndex = ref(0);
const suppressDismiss = ref(false);
const contentViewport = ref<HTMLElement | null>(null);
const currentStep = computed(() => props.steps[currentIndex.value] || null);
const isFirstStep = computed(() => currentIndex.value === 0);
const isLastStep = computed(() => currentIndex.value === Math.max(0, props.steps.length - 1));
const progressValue = computed(() => (
    props.steps.length > 0 ? ((currentIndex.value + 1) / props.steps.length) * 100 : 0
));

const open = computed({
    get: () => props.modelValue,
    set: (value: boolean) => {
        emit('update:modelValue', value);
        if (!value && !suppressDismiss.value) emit('dismiss');
        suppressDismiss.value = false;
    },
});

function announceStep() {
    if (currentStep.value) emit('stepChange', currentStep.value, currentIndex.value);
}

function resetContentScroll() {
    nextTick(() => contentViewport.value?.scrollTo({ top: 0 }));
}

function moveNext() {
    if (isLastStep.value) return;
    currentIndex.value += 1;
    resetContentScroll();
    announceStep();
}

function moveBack() {
    if (isFirstStep.value) return;
    currentIndex.value -= 1;
    resetContentScroll();
    announceStep();
}

function dismissGuide() {
    suppressDismiss.value = true;
    emit('dismiss');
    emit('update:modelValue', false);
    suppressDismiss.value = false;
}

function startSetup() {
    suppressDismiss.value = true;
    emit('start');
    emit('update:modelValue', false);
    suppressDismiss.value = false;
}

watch(
    () => props.modelValue,
    (value) => {
        if (!value) return;
        currentIndex.value = 0;
        resetContentScroll();
        nextTick(announceStep);
    },
);
</script>

<template>
    <Dialog v-model:open="open">
        <DialogContent
            class="!flex !max-h-[calc(100svh-6.5rem)] !w-[calc(100%-1rem)] !max-w-[calc(100%-1rem)] flex-col !gap-0 overflow-hidden !p-0 sm:!max-h-[min(92vh,56rem)] sm:!w-[calc(100%-2rem)] sm:!max-w-[70rem]"
        >
            <DialogHeader class="shrink-0 !gap-2 border-b border-border/60 px-4 pb-3 pt-4 !text-left sm:!gap-3 sm:px-7 sm:pb-5 sm:pt-6">
                <div class="flex items-center justify-between gap-4 pr-8">
                    <p class="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Feature guide</p>
                    <p v-if="steps.length" class="text-xs text-muted-foreground">{{ currentIndex + 1 }} of {{ steps.length }}</p>
                </div>
                <DialogTitle class="pr-7 text-lg leading-6 tracking-tight sm:pr-0 sm:text-2xl">{{ title }}</DialogTitle>
                <DialogDescription v-if="description" class="max-w-2xl text-sm leading-5 sm:leading-6">
                    {{ description }}
                </DialogDescription>
                <Progress v-if="steps.length" :model-value="progressValue" class="h-1.5" />
            </DialogHeader>

            <div ref="contentViewport" class="min-h-0 flex-1 overscroll-contain overflow-y-auto px-4 py-4 sm:px-7 sm:py-6">
                <div v-if="loading" class="grid gap-5 lg:grid-cols-[minmax(0,1.12fr)_minmax(20rem,0.88fr)] lg:gap-10">
                    <Skeleton class="h-40 w-full rounded-2xl sm:aspect-video sm:h-auto sm:min-h-56" />
                    <div class="space-y-4 py-2">
                        <Skeleton class="h-4 w-24" />
                        <Skeleton class="h-8 w-4/5" />
                        <Skeleton class="h-4 w-full" />
                        <Skeleton class="h-4 w-5/6" />
                    </div>
                </div>

                <EmptyState
                    v-else-if="!currentStep"
                    embedded
                    :title="emptyTitle"
                    :description="emptyDescription"
                />

                <div v-else class="grid items-start gap-4 sm:gap-6 lg:grid-cols-[minmax(0,1.12fr)_minmax(20rem,0.88fr)] lg:gap-10">
                    <div
                        class="min-w-0 lg:order-none"
                        :class="currentStep.video?.src ? 'order-1' : 'order-2'"
                    >
                        <video
                            v-if="currentStep.video?.src"
                            class="aspect-video w-full rounded-2xl border border-border/70 bg-muted object-cover shadow-sm"
                            controls
                            playsinline
                            preload="metadata"
                            :poster="currentStep.video.poster"
                            :aria-label="currentStep.video.title"
                        >
                            <source :src="currentStep.video.src">
                            <track
                                v-if="currentStep.video.captionsSrc"
                                kind="captions"
                                :src="currentStep.video.captionsSrc"
                                srclang="en"
                                label="English"
                                default
                            >
                            Your browser does not support embedded video.
                        </video>

                        <div
                            v-else
                            class="flex min-h-40 items-center justify-center overflow-hidden rounded-2xl border border-border/70 bg-muted/35 py-4 sm:aspect-video sm:min-h-56 sm:py-0"
                        >
                            <slot name="visual" :step="currentStep" :index="currentIndex">
                                <div class="max-w-sm space-y-3 px-6 text-center">
                                    <PlayCircle class="mx-auto size-10 text-primary" />
                                    <p class="text-sm font-medium text-foreground">{{ currentStep.title }}</p>
                                    <p class="text-xs leading-5 text-muted-foreground">A recorded walkthrough can be added here without changing the guide flow.</p>
                                </div>
                            </slot>
                        </div>
                    </div>

                    <div
                        class="min-w-0 space-y-4 lg:order-none lg:space-y-5 lg:py-1"
                        :class="currentStep.video?.src ? 'order-2' : 'order-1'"
                    >
                        <div class="space-y-2">
                            <p v-if="currentStep.eyebrow" class="text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                                {{ currentStep.eyebrow }}
                            </p>
                            <h3 class="text-lg font-semibold leading-6 tracking-tight text-foreground sm:text-xl">{{ currentStep.title }}</h3>
                            <p class="text-sm leading-5 text-muted-foreground sm:leading-6">{{ currentStep.description }}</p>
                        </div>

                        <div v-if="currentStep.points?.length" class="divide-y divide-border/50 border-y border-border/60">
                            <div v-for="point in currentStep.points" :key="point" class="flex items-start gap-3 py-2.5 sm:py-3">
                                <CheckCircle2 class="mt-0.5 size-4 shrink-0 text-primary" />
                                <p class="text-sm leading-5 text-foreground/88">{{ point }}</p>
                            </div>
                        </div>

                        <slot name="step" :step="currentStep" :index="currentIndex" />
                    </div>
                </div>
            </div>

            <DialogFooter class="shrink-0 !flex-row items-center !justify-between gap-2 border-t border-border/60 bg-card px-3 py-3 sm:px-7 sm:py-4">
                <Button type="button" variant="ghost" class="h-9 px-2 sm:w-auto sm:px-4" @click="dismissGuide">{{ skipLabel }}</Button>
                <div class="flex items-center justify-end gap-2">
                    <Button v-if="!isFirstStep" type="button" variant="outline" @click="moveBack">{{ backLabel }}</Button>
                    <Button v-if="!isLastStep" type="button" :disabled="loading || !currentStep" @click="moveNext">{{ nextLabel }}</Button>
                    <Button v-else type="button" :disabled="loading || !currentStep" @click="startSetup">{{ startLabel }}</Button>
                </div>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
